import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Badge, Card, PageHeader, useCreateEntity, type ProjectWorkItem } from "@pericles/framework-frontend";
import { appUiSpec } from "@pericles/app-project-requirements-config";

import { bootstrapProjectBaseline } from "../api/projectBootstrapClient";
import { fetchProjectById, type ProjectDetail } from "../api/projectsClient";
import { generateProjectContext, type ProjectContextSource } from "../api/projectContextClient";
import { fetchProjectWorkItems } from "../api/projectWorkItemsClient";
import {
  JarvisRunSpecApiError,
  fetchJarvisRunSpec,
  type JarvisRunSpecValidationReport,
} from "../api/jarvisRunSpecClient";
import { useFeatureFlag } from "../config/featureFlags";
import { useAuth } from "../hooks/auth";
import {
  buildDefinitionOfReady,
  describeMissingReadyItems,
  validationBadgeVariant,
  type DefinitionOfReadyResult,
  type JarvisRunSpecLike,
} from "../domain/jarvisRunSpec";

import { ProjectPreDiscoveryPanel } from "./ProjectPreDiscoveryPanel";
import { ProjectCoverageTab } from "./ProjectCoverageTab";
import { ProjectMeetingsTab } from "./ProjectMeetingsTab";
import { ProjectDiscoveryPanel } from "./ProjectDiscoveryPanel";
import { ProjectSpecSectionsTab } from "./ProjectSpecSectionsTab";
import { ProjectWorkplanTab } from "./ProjectWorkplanTab";
import { RequirementsListScreen } from "./RequirementsListScreen";
import { RequirementDetailScreen } from "./RequirementDetailScreen";
import { ProjectBriefPanel } from "./ProjectBriefPanel";
import { ContextUsedBlock } from "./ContextUsedBlock";
import { ProjectCockpitTab } from "./ProjectCockpitTab";
import { ProjectJarvisBenchTab } from "./ProjectJarvisBenchTab";
import { Button, Input, PageShell } from "./ui";

type DetailTabKey =
  | "overview"
  | "requirements"
  | "specs"
  | "discovery"
  | "preDiscovery"
  | "workplan"
  | "coverage"
  | "meetings"
  | "brief"
  | "bench"
  | "cockpit";

interface RequirementDraftFormState {
  title: string;
  description: string;
  sourceType: "manual" | "pdf_summary" | "meeting_ai" | "other_ai";
  generationTarget: "backend_module" | "frontend_module" | "full_stack_module";
  sourceMeetingId: string;
}

const DEFAULT_REQUIREMENT_DRAFT: RequirementDraftFormState = {
  title: "",
  description: "",
  sourceType: "manual",
  generationTarget: "full_stack_module",
  sourceMeetingId: "",
};

function formatDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function getCustomerName(customer: ProjectDetail["customer"]): string {
  if (!customer) return "";
  if (typeof customer === "string") return customer;
  if (typeof customer === "object") {
    if ("name" in customer && typeof (customer as any).name === "string") {
      return (customer as any).name as string;
    }
    if ("title" in customer && typeof (customer as any).title === "string") {
      return (customer as any).title as string;
    }
  }

  return String(customer);
}

function resolveTabFromSearchParams(params: URLSearchParams, cockpitEnabled: boolean): DetailTabKey {
  const tab = params.get("tab");
  if (
    tab === "requirements" ||
    tab === "specs" ||
    tab === "discovery" ||
    tab === "preDiscovery" ||
    tab === "workplan" ||
    tab === "coverage" ||
    tab === "meetings" ||
    tab === "brief" ||
    tab === "bench" ||
    (cockpitEnabled && tab === "cockpit")
  ) {
    return tab;
  }
  return "overview";
}

export function ProjectDetailScreen(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTenantId } = useAuth();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const searchParamsKey = useMemo(() => searchParams.toString(), [searchParams]);
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTabKey>("overview");
  const [cockpitEnabled] = useFeatureFlag("FEATURE_COCKPIT_V01", true);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [selectedRequirementId, setSelectedRequirementId] = useState<string | null>(null);
  const [requirementsRefreshKey, setRequirementsRefreshKey] = useState<number>(0);
  const [workItemCoverageByRequirementId, setWorkItemCoverageByRequirementId] = useState<Record<string, number>>({});
  const [isLoadingRequirementCoverage, setIsLoadingRequirementCoverage] = useState(false);
  const [isCreateRequirementModalOpen, setIsCreateRequirementModalOpen] = useState(false);
  const [createRequirementDraft, setCreateRequirementDraft] =
    useState<RequirementDraftFormState>(DEFAULT_REQUIREMENT_DRAFT);
  const [createRequirementError, setCreateRequirementError] = useState<string | null>(null);
  const [projectRefreshKey, setProjectRefreshKey] = useState<number>(0);
  const [isGeneratingContext, setIsGeneratingContext] = useState(false);
  const [contextMessage, setContextMessage] = useState<string | null>(null);
  const [showContext, setShowContext] = useState(false);
  const [jarvisTicketId, setJarvisTicketId] = useState("");
  const [jarvisTicketOwner, setJarvisTicketOwner] = useState("");
  const [jarvisExporting, setJarvisExporting] = useState(false);
  const [jarvisExportError, setJarvisExportError] = useState<string | null>(null);
  const [jarvisExportSuccess, setJarvisExportSuccess] = useState<string | null>(null);
  const [jarvisExportRemediation, setJarvisExportRemediation] = useState<string[]>([]);
  const [jarvisExportSpec, setJarvisExportSpec] = useState<JarvisRunSpecLike | null>(null);
  const [jarvisExportValidationReport, setJarvisExportValidationReport] =
    useState<JarvisRunSpecValidationReport | null>(null);
  const [jarvisExportDefinition, setJarvisExportDefinition] = useState<DefinitionOfReadyResult | null>(null);
  const createRequirement = useCreateEntity<Record<string, unknown>, Record<string, unknown>>({
    entity: "Requirement",
  });

  useEffect(() => {
    setActiveTab(resolveTabFromSearchParams(searchParams, cockpitEnabled));
  }, [cockpitEnabled, projectId, searchParamsKey, searchParams]);

  useEffect(() => {
    const requirementId = searchParams.get("requirementId");
    setSelectedRequirementId(requirementId);
  }, [projectId, searchParamsKey, searchParams]);

  useEffect(() => {
    if (!projectId) {
      setWorkItemCoverageByRequirementId({});
      return;
    }

    let cancelled = false;
    const currentProjectId = projectId;

    async function loadWorkItemCoverage() {
      setIsLoadingRequirementCoverage(true);
      try {
        const items = await fetchProjectWorkItems(currentProjectId);
        if (cancelled) return;
        const next: Record<string, number> = {};
        items.forEach((item) => {
          (item.linkedRequirementIds ?? []).forEach((requirementId) => {
            const key = String(requirementId);
            next[key] = (next[key] ?? 0) + 1;
          });
        });
        setWorkItemCoverageByRequirementId(next);
      } catch (coverageError) {
        if (!cancelled) {
          console.error("Failed to load requirement/work item coverage", coverageError);
          setWorkItemCoverageByRequirementId({});
        }
      } finally {
        if (!cancelled) {
          setIsLoadingRequirementCoverage(false);
        }
      }
    }

    void loadWorkItemCoverage();
    return () => {
      cancelled = true;
    };
  }, [activeTab, projectId, requirementsRefreshKey, searchParamsKey]);

  const refreshProject = useCallback(() => setProjectRefreshKey((current) => current + 1), []);

  useEffect(() => {
    if (!projectId) {
      setError("Missing project id");
      setProject(null);
      return;
    }

    let cancelled = false;
    const currentProjectId = projectId;
    async function loadProject() {
      setIsLoading(true);
      setError(null);
      try {
        const details = await fetchProjectById(currentProjectId);
        if (!cancelled) {
          setProject(details);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load project");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadProject();

    return () => {
      cancelled = true;
    };
  }, [projectId, projectRefreshKey]);

  const projectName = project?.name ?? `Project ${projectId ?? ""}`;
  const customerName = useMemo(() => getCustomerName(project?.customer), [project?.customer]);
  const requirementEntity = useMemo(() => appUiSpec.entities.find((item) => item.entityName === "Requirement"), []);

  const templateFocusTitle = searchParams.get("templateTitle");
  const templateFocusCode = searchParams.get("templateCode");
  const shouldOpenWorkItemForm = searchParams.get("newWorkItem") === "1";

  const openCreateRequirementModal = useCallback(() => {
    setCreateRequirementError(null);
    setCreateRequirementDraft({
      ...DEFAULT_REQUIREMENT_DRAFT,
      ...(templateFocusTitle ? { title: templateFocusTitle } : {}),
    });
    setIsCreateRequirementModalOpen(true);
  }, [templateFocusTitle]);

  const closeCreateRequirementModal = useCallback(() => {
    setCreateRequirementError(null);
    setCreateRequirementDraft(DEFAULT_REQUIREMENT_DRAFT);
    setIsCreateRequirementModalOpen(false);
  }, []);

  const handleRequirementDraftChange = useCallback(
    (field: keyof RequirementDraftFormState, value: string) => {
      setCreateRequirementError(null);
      setCreateRequirementDraft((previous) => ({ ...previous, [field]: value }));
    },
    [],
  );

  const updateSearchParams = (params: URLSearchParams, options?: { replace?: boolean }) => {
    const searchString = params.toString();
    navigate(`${location.pathname}${searchString ? `?${searchString}` : ""}`, {
      replace: options?.replace ?? true,
    });
  };

  async function handleBootstrapBaseline() {
    if (!projectId) return;

    const confirmed = window.confirm(
      "This will create baseline requirements and checklist items for this project using Pericles templates. Continue?",
    );
    if (!confirmed) return;

    try {
      setIsBootstrapping(true);
      const result = await bootstrapProjectBaseline(projectId, {
        includeRequirements: true,
        includeChecklist: true,
      });

      alert(
        `Baseline created for project ${result.projectId}:\n` +
          `- Requirements: ${result.createdRequirements} created, ${result.skippedRequirements} skipped\n` +
          `- Checklist items: ${result.createdChecklistItems} created, ${result.skippedChecklistItems} skipped`,
      );
    } catch (baselineError: any) {
      console.error(baselineError);
      alert(baselineError?.message ?? "Failed to bootstrap project baseline.");
    } finally {
      setIsBootstrapping(false);
    }
  }

  async function handleGenerateContext() {
    if (!projectId) return;
    setIsGeneratingContext(true);
    setContextMessage(null);

    try {
      const response = await generateProjectContext(projectId, { force: true });
      setContextMessage(`Context updated with ${response.model}`);
      refreshProject();
    } catch (contextError: any) {
      const message = contextError instanceof Error ? contextError.message : "Failed to generate context";
      setContextMessage(message);
    } finally {
      setIsGeneratingContext(false);
    }
  }

  async function handleExportJarvisSpec() {
    if (!projectId) return;
    if (!jarvisTicketId.trim()) {
      setJarvisExportError("Provide ticket id");
      setJarvisExportSuccess(null);
      return;
    }

    setJarvisExportError(null);
    setJarvisExportSuccess(null);
    setJarvisExportRemediation([]);
    setJarvisExportSpec(null);
    setJarvisExportDefinition(null);
    setJarvisExportValidationReport(null);
    setJarvisExporting(true);

    try {
      const ticket = jarvisTicketId.trim();
      const response = await fetchJarvisRunSpec(projectId, {
        ticketId: ticket,
        title: projectName,
        owner: jarvisTicketOwner.trim() || undefined,
      });
      const spec = response.spec as JarvisRunSpecLike;
      const exportWorkItem = { owner: jarvisTicketOwner } as ProjectWorkItem;
      const definitionOfReady = buildDefinitionOfReady(spec, exportWorkItem);
      setJarvisExportSpec(spec);
      setJarvisExportDefinition(definitionOfReady);
      setJarvisExportValidationReport(response.validationReport);

      if (!response.validationReport.valid) {
        setJarvisExportError("Jarvis run spec failed validation.");
        setJarvisExportRemediation([
          "Download the validation report to review schema errors.",
          "Align the project inputs with specs/jarvis/run-spec.schema.json and retry.",
        ]);
        return;
      }

      if (!definitionOfReady.ready) {
        setJarvisExportError("Jarvis export blocked: please complete the Definition of Ready.");
        setJarvisExportRemediation(describeMissingReadyItems(definitionOfReady));
        return;
      }

      const filename = `${ticket}.jarvis.json`;
      const blob = new Blob([JSON.stringify(response.spec, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      setJarvisExportSuccess(`${filename} ready for download.`);
    } catch (error: any) {
      if (error instanceof JarvisRunSpecApiError) {
        const spec = (error.spec ?? {}) as JarvisRunSpecLike;
        const exportWorkItem = { owner: jarvisTicketOwner } as ProjectWorkItem;
        const definitionOfReady = buildDefinitionOfReady(spec, exportWorkItem);
        if (error.validationReport) {
          setJarvisExportSpec(spec);
          setJarvisExportDefinition(definitionOfReady);
          setJarvisExportValidationReport(error.validationReport);
        }
        setJarvisExportError(error.message);
        setJarvisExportRemediation(error.remediationSteps ?? []);
        return;
      }

      const message = error instanceof Error ? error.message : "Failed to export Jarvis run spec";
      setJarvisExportError(message);
    } finally {
      setJarvisExporting(false);
    }
  }

  useEffect(() => {
    if (!jarvisExportSpec) return;
    const exportWorkItem = { owner: jarvisTicketOwner } as ProjectWorkItem;
    setJarvisExportDefinition(buildDefinitionOfReady(jarvisExportSpec, exportWorkItem));
  }, [jarvisExportSpec, jarvisTicketOwner]);

  const jarvisValidationLabel = jarvisExportValidationReport
    ? jarvisExportValidationReport.valid
      ? "Validation PASS"
      : "Validation FAIL"
    : "Awaiting validation";
  const jarvisDefinitionMissing = jarvisExportDefinition ? describeMissingReadyItems(jarvisExportDefinition) : [];

  const downloadJarvisValidationReport = () => {
    if (!jarvisExportValidationReport) return;
    const blob = new Blob([JSON.stringify(jarvisExportValidationReport, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `jarvis-run-spec-${jarvisTicketId.trim() || "project"}-validation-report.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleTabChange = (nextTab: DetailTabKey) => {
    if (nextTab === "cockpit" && !cockpitEnabled) {
      nextTab = "overview";
    }
    setActiveTab(nextTab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", nextTab);

    if (nextTab !== "requirements" && nextTab !== "workplan") {
      nextParams.delete("requirementId");
      setSelectedRequirementId(null);
    }

    if (nextTab !== "requirements") {
      nextParams.delete("templateTitle");
      nextParams.delete("templateCode");
    }

    if (nextTab !== "workplan") {
      nextParams.delete("newWorkItem");
    }

    updateSearchParams(nextParams);
  };

  const handleRequirementSelect = (requirementId: string | null) => {
    setSelectedRequirementId(requirementId);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "requirements");
    if (requirementId) {
      nextParams.set("requirementId", requirementId);
    } else {
      nextParams.delete("requirementId");
    }
    updateSearchParams(nextParams);
  };

  const openWorkplanForRequirement = useCallback(
    (requirementId: string, options?: { openCreate?: boolean }) => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "workplan");
      params.set("requirementId", requirementId);
      if (options?.openCreate) {
        params.set("newWorkItem", "1");
      } else {
        params.delete("newWorkItem");
      }
      setActiveTab("workplan");
      updateSearchParams(params, { replace: false });
    },
    [searchParams, updateSearchParams],
  );

  useEffect(() => {
    if (!cockpitEnabled && activeTab === "cockpit") {
      handleTabChange("overview");
    }
  }, [activeTab, cockpitEnabled]);

  const handleCreateRequirementSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!projectId) {
        setCreateRequirementError("Project id is required.");
        return;
      }

      const titleValue = createRequirementDraft.title.trim();
      const descriptionValue = createRequirementDraft.description.trim();
      if (!titleValue || !descriptionValue) {
        setCreateRequirementError("Title and description are required.");
        return;
      }

      const payload: Record<string, unknown> = {
        projectId,
        title: titleValue,
        description: descriptionValue,
        sourceType: createRequirementDraft.sourceType,
        generationTarget: createRequirementDraft.generationTarget,
      };

      const sourceMeetingId = createRequirementDraft.sourceMeetingId.trim();
      if (sourceMeetingId.length > 0) {
        payload.sourceMeetingId = sourceMeetingId;
      }

      if (activeTenantId) {
        payload.tenantId = activeTenantId;
      }

      const created = await createRequirement.create(payload);
      if (created === undefined) {
        setCreateRequirementError(
          createRequirement.error instanceof Error
            ? createRequirement.error.message
            : "Failed to create requirement.",
        );
        return;
      }

      const createdId =
        typeof created === "object" && created !== null && "id" in created && (created as any).id != null
          ? String((created as any).id)
          : null;

      setRequirementsRefreshKey((current) => current + 1);
      if (createdId) {
        handleRequirementSelect(createdId);
      }
      closeCreateRequirementModal();
    },
    [
      activeTenantId,
      closeCreateRequirementModal,
      createRequirement,
      createRequirementDraft,
      projectId,
    ],
  );

  const contextStatus = project?.contextGenerationStatus ?? "idle";
  const contextSources = (project?.contextSources as ProjectContextSource[] | undefined) ?? [];
  const contextGeneratedAt = project?.contextGeneratedAt;
  const contextError = project?.contextGenerationError;
  const customerData = project && typeof project.customer === "object" && project.customer !== null ? (project.customer as any) : null;
  const contextRefreshKey = useMemo(
    () =>
      [
        project?.contextGenerationStatus,
        project?.contextGeneratedAt,
        project?.preDiscoveryQuestionsStatus,
        project?.preDiscoveryQuestionsGeneratedAt,
        customerData?.contextGenerationStatus,
        customerData?.contextGeneratedAt,
      ]
        .filter(Boolean)
        .join("|"),
    [
      customerData?.contextGeneratedAt,
      customerData?.contextGenerationStatus,
      project?.contextGeneratedAt,
      project?.contextGenerationStatus,
      project?.preDiscoveryQuestionsGeneratedAt,
      project?.preDiscoveryQuestionsStatus,
    ],
  );

  const overviewContent = (
    <div className="project-detail-screen__overview flex flex-col gap-4">
      <div className="project-detail-screen__overview-grid grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <Card className="project-detail-screen__card project-detail-screen__card--details">
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-slate-900">Project details</h2>
              {project?.status ? (
                <span className="rounded bg-slate-100 px-3 py-1 text-xs font-semibold uppercase text-slate-700">
                  {project.status}
                </span>
              ) : null}
            </div>

            <div className="grid gap-2 md:grid-cols-2">
              <div>
                <div className="text-sm text-slate-500">Customer</div>
                <div className="text-sm font-semibold text-slate-900">{customerName || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Code</div>
                <div className="text-sm font-semibold text-slate-900">{project?.code || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Start date</div>
                <div className="text-sm font-semibold text-slate-900">{formatDate(project?.startDate) || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">End date</div>
                <div className="text-sm font-semibold text-slate-900">{formatDate(project?.endDate) || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Created</div>
                <div className="text-sm font-semibold text-slate-900">{formatDate(project?.createdAt) || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Updated</div>
                <div className="text-sm font-semibold text-slate-900">{formatDate(project?.updatedAt) || "-"}</div>
              </div>
            </div>

            {project?.description ? (
              <div className="rounded bg-slate-50 p-3 text-sm text-slate-700">{project.description}</div>
            ) : null}
          </div>
        </Card>

        <Card className="project-detail-screen__card project-detail-screen__card--bootstrap">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-base font-semibold text-slate-900">Bootstrap baseline</div>
                <p className="text-sm text-slate-700">
                  Generate baseline requirements and checklist items for this project.
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleBootstrapBaseline}
                disabled={isBootstrapping}
              >
                {isBootstrapping ? "Bootstrapping..." : "Bootstrap baseline (requirements & checklist)"}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Card className="project-detail-screen__card project-detail-screen__card--context">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-slate-900">Project context</div>
              <p className="text-sm text-slate-700">
                Status: {contextStatus}
                {contextGeneratedAt ? ` — generated ${formatDate(contextGeneratedAt)}` : ""}
              </p>
              {contextError ? <p className="text-sm text-red-600">{contextError}</p> : null}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleGenerateContext}
              disabled={isGeneratingContext || !projectId}
            >
              {isGeneratingContext ? "Generating..." : "Generate project context (AI)"}
            </Button>
          </div>
          {contextMessage ? <p className="text-sm text-slate-700">{contextMessage}</p> : null}
          <Button
            type="button"
            variant="link"
            className="text-sm text-slate-700"
            onClick={() => setShowContext((current) => !current)}
          >
            {showContext ? "Hide" : "Show"} context summary
          </Button>
          {showContext ? (
            <div className="rounded bg-slate-50 p-3 text-sm text-slate-800">
              {project?.context?.trim() ? project.context : "No context captured yet."}
              {contextSources.length ? (
                <div className="mt-2 text-xs text-slate-600">
                  Sources: {contextSources.map((source) => source.title ?? source.type).filter(Boolean).join(", ")}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      <Card className="project-detail-screen__card project-detail-screen__card--jarvis">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-slate-900">Jarvis run spec</div>
              <p className="text-sm text-slate-700">
                Export a sanitized, schema-validated run spec for the current project.
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void handleExportJarvisSpec()}
              disabled={jarvisExporting || !projectId}
            >
              {jarvisExporting ? "Exporting..." : "Export Jarvis spec"}
            </Button>
          </div>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
            <div className="flex flex-col gap-1 md:min-w-[260px] md:flex-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-600" htmlFor="jarvis-ticket">
                Ticket id
              </label>
              <Input
                id="jarvis-ticket"
                type="text"
                placeholder="e.g. STUDIO-123"
                value={jarvisTicketId}
                onChange={(event) => setJarvisTicketId(event.target.value)}
              />
              {jarvisTicketId.trim() === "" ? (
                <p className="text-xs text-slate-600">Provide ticket id</p>
              ) : null}
              {jarvisExportError ? <p className="text-xs text-red-600">{jarvisExportError}</p> : null}
              {jarvisExportSuccess ? <p className="text-xs text-green-700">{jarvisExportSuccess}</p> : null}
            </div>
            <div className="flex flex-col gap-1 md:min-w-[220px]">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-600" htmlFor="jarvis-owner">
                WorkItem owner
              </label>
              <Input
                id="jarvis-owner"
                type="text"
                placeholder="e.g. alex"
                value={jarvisTicketOwner}
                onChange={(event) => setJarvisTicketOwner(event.target.value)}
              />
              {jarvisTicketOwner.trim() === "" ? (
                <p className="text-xs text-slate-600">Owner required for Definition of Ready.</p>
              ) : null}
            </div>
          </div>
          {jarvisExportRemediation.length ? (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="font-semibold">Jarvis export blocked</div>
              <ul className="ml-4 mt-2 list-disc space-y-1">
                {jarvisExportRemediation.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex flex-col gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            <div className="flex flex-wrap items-center gap-2">
              <Badge label={jarvisValidationLabel} variant={validationBadgeVariant(jarvisExportValidationReport ?? undefined)} />
              {jarvisExportValidationReport ? (
                <Button type="button" variant="link" className="text-blue-700" onClick={downloadJarvisValidationReport}>
                  Download validation report
                </Button>
              ) : (
                <span className="text-slate-500">Export to validate</span>
              )}
            </div>
            <div className="rounded border border-slate-200 bg-white p-3 text-[11px] text-slate-700">
              <div className="font-semibold text-slate-800">Definition of Ready</div>
              {jarvisExportDefinition ? (
                <ul className="mt-1 space-y-1">
                  {jarvisExportDefinition.items.map((itemState) => (
                    <li key={itemState.id} className="flex items-center gap-1">
                      <span
                        className={`h-2 w-2 rounded-full ${itemState.satisfied ? "bg-green-500" : "bg-amber-500"}`}
                        aria-hidden
                      />
                      <span className={itemState.satisfied ? "text-slate-700" : "text-amber-700"}>
                        {itemState.label}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-1 text-slate-600">Export to compute readiness.</div>
              )}
              {jarvisDefinitionMissing.length ? (
                <div className="mt-2 text-[11px] text-amber-700">
                  Missing: {jarvisDefinitionMissing.join(", ")}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Card>

      {projectId ? <ContextUsedBlock projectId={projectId} refreshKey={contextRefreshKey} /> : null}
    </div>
  );

  const requirementsContent = projectId ? (
    <div className="project-detail-screen__requirements grid gap-4 lg:grid-cols-[1.4fr_1fr]">
      <RequirementsListScreen
        key={`${projectId}-${requirementsRefreshKey}`}
        entityName="Requirement"
        title="Project requirements"
        filters={{ projectId } as any}
        projectIdFilter={projectId}
        hideProjectFilter
        workItemCoverageByRequirementId={workItemCoverageByRequirementId}
        onRowClick={(row: any) => handleRequirementSelect((row as any)?.id ? String((row as any).id) : null)}
        renderActions={(row) => {
          const requirementId = String(row.id);
          const linkedWorkItemCount = workItemCoverageByRequirementId[requirementId] ?? 0;
          return (
            <div className="flex flex-col items-end gap-1">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={linkedWorkItemCount === 0 || isLoadingRequirementCoverage}
                onClick={(event) => {
                  event.stopPropagation();
                  openWorkplanForRequirement(requirementId);
                }}
              >
                Open work items ({linkedWorkItemCount})
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  openWorkplanForRequirement(requirementId, { openCreate: true });
                }}
              >
                Create work item
              </Button>
            </div>
          );
        }}
      />

      <div className="project-detail-screen__requirements-side flex flex-col gap-4">
        {templateFocusTitle ? (
          <Card>
            <div className="flex flex-col gap-2 text-sm text-slate-800">
              <div className="text-base font-semibold text-slate-900">Create requirement for template</div>
              <div>
                Target template: {templateFocusCode ? `${templateFocusCode} — ` : ""}
                <span className="font-semibold">{templateFocusTitle}</span>
              </div>
              <div className="text-slate-700">
                Use the form below to add a new requirement linked to this template.
              </div>
            </div>
          </Card>
        ) : null}

        <Card>
          <div className="flex flex-col gap-2">
            <div className="text-sm text-slate-700">
              Create a requirement in a dedicated modal. Project and tenant are injected automatically.
            </div>
            <div>
              <Button type="button" variant="primary" size="sm" onClick={openCreateRequirementModal} disabled={!projectId}>
                New requirement
              </Button>
            </div>
          </div>
        </Card>

        {requirementEntity && selectedRequirementId ? (
          <RequirementDetailScreen
            key={`project-req-detail-${selectedRequirementId}-${requirementsRefreshKey}`}
            entity={requirementEntity}
            id={selectedRequirementId}
            onBack={() => handleRequirementSelect(null)}
            onDeleted={() => {
              handleRequirementSelect(null);
              setRequirementsRefreshKey((current) => current + 1);
            }}
          />
        ) : (
          <Card>
            <div className="p-4 text-sm text-slate-700">Select a requirement to view its details.</div>
          </Card>
        )}

        {isCreateRequirementModalOpen ? (
          <div
            className="project-detail-screen__modal-backdrop fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 p-4"
            onClick={(event) => {
              if (event.target === event.currentTarget && !createRequirement.isSubmitting) {
                closeCreateRequirementModal();
              }
            }}
          >
            <div className="project-detail-screen__modal w-full max-w-3xl rounded-xl border border-slate-200 bg-white shadow-2xl">
              <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
                <div className="flex flex-col gap-1">
                  <h3 className="text-lg font-semibold text-slate-900">Create Requirement</h3>
                  <p className="text-sm text-slate-600">
                    Project: {projectId} {activeTenantId ? `| Tenant: ${activeTenantId}` : ""}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={closeCreateRequirementModal}
                  disabled={createRequirement.isSubmitting}
                >
                  Close
                </Button>
              </div>

              <form className="flex flex-col gap-4 px-5 py-5" onSubmit={handleCreateRequirementSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Title *
                    <Input
                      type="text"
                      value={createRequirementDraft.title}
                      onChange={(event) => handleRequirementDraftChange("title", event.target.value)}
                      placeholder="Requirement title"
                      required
                    />
                  </label>

                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Source type *
                    <select
                      value={createRequirementDraft.sourceType}
                      onChange={(event) => handleRequirementDraftChange("sourceType", event.target.value)}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      required
                    >
                      <option value="manual">Manual</option>
                      <option value="pdf_summary">PDF summary</option>
                      <option value="meeting_ai">Meeting AI</option>
                      <option value="other_ai">Other AI</option>
                    </select>
                  </label>
                </div>

                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Description *
                  <textarea
                    value={createRequirementDraft.description}
                    onChange={(event) => handleRequirementDraftChange("description", event.target.value)}
                    className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="Describe the business and technical requirement"
                    required
                  />
                </label>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Generation target *
                    <select
                      value={createRequirementDraft.generationTarget}
                      onChange={(event) => handleRequirementDraftChange("generationTarget", event.target.value)}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      required
                    >
                      <option value="full_stack_module">Full-stack module</option>
                      <option value="backend_module">Backend module</option>
                      <option value="frontend_module">Frontend module</option>
                    </select>
                  </label>

                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Source meeting ID (optional)
                    <Input
                      type="text"
                      value={createRequirementDraft.sourceMeetingId}
                      onChange={(event) => handleRequirementDraftChange("sourceMeetingId", event.target.value)}
                      placeholder="meeting_xxx"
                    />
                  </label>
                </div>

                {createRequirementError ? (
                  <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {createRequirementError}
                  </p>
                ) : null}

                <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeCreateRequirementModal}
                    disabled={createRequirement.isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" disabled={createRequirement.isSubmitting}>
                    {createRequirement.isSubmitting ? "Creating..." : "Create requirement"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  ) : (
    <Card>
      <div className="p-4 text-sm text-slate-700">Project id is required to load requirements.</div>
    </Card>
  );

  const specsContent = projectId ? (
    <ProjectSpecSectionsTab projectId={projectId} />
  ) : (
    <Card>
      <div className="p-4 text-sm text-slate-700">Project id is required to load spec sections.</div>
    </Card>
  );

  const coverageContent = projectId ? (
    <ProjectCoverageTab projectId={projectId} />
  ) : (
    <Card>
      <div className="p-4 text-sm text-slate-700">Project id is required to load coverage data.</div>
    </Card>
  );

  const discoveryContent = projectId ? (
    <ProjectDiscoveryPanel projectId={projectId} contextRefreshKey={contextRefreshKey} />
  ) : (
    <Card>
      <div className="p-4 text-sm text-slate-700">Project id is required to load discovery inputs.</div>
    </Card>
  );

  const preDiscoveryContent = projectId ? (
    <ProjectPreDiscoveryPanel projectId={projectId} contextRefreshKey={contextRefreshKey} />
  ) : (
    <Card>
      <div className="p-4 text-sm text-slate-700">Project id is required to generate pre-discovery questions.</div>
    </Card>
  );

  const meetingsContent = projectId ? (
    <ProjectMeetingsTab projectId={projectId} />
  ) : (
    <Card>
      <div className="p-4 text-sm text-slate-700">Project id is required to load meeting data.</div>
    </Card>
  );

  const briefContent = projectId ? (
    <ProjectBriefPanel projectId={projectId} />
  ) : (
    <Card>
      <div className="p-4 text-sm text-slate-700">Project id is required to load project brief.</div>
    </Card>
  );

  const benchContent = projectId ? (
    <ProjectJarvisBenchTab />
  ) : (
    <Card>
      <div className="p-4 text-sm text-slate-700">Project id is required to run Jarvis bench.</div>
    </Card>
  );

  const workplanContent = projectId ? (
    <ProjectWorkplanTab
      projectId={projectId}
      initialLinkedRequirementId={selectedRequirementId}
      shouldOpenCreate={shouldOpenWorkItemForm}
      onRequestCloseCreate={() => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("newWorkItem");
        updateSearchParams(nextParams);
      }}
    />
  ) : (
    <Card>
      <div className="p-4 text-sm text-slate-700">Project id is required to load workplan data.</div>
    </Card>
  );

  if (isLoading) {
    return (
      <PageShell>
        <div className="project-detail-screen">
          <PageHeader title="Loading project..." />
          <Card>
            <div className="p-4 text-sm text-slate-700">Fetching project details.</div>
          </Card>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div className="project-detail-screen">
          <PageHeader title="Project" onBack={() => navigate(-1)} />
          <Card>
            <div className="p-4 text-sm text-red-700">{error}</div>
          </Card>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="project-detail-screen">
        <PageHeader
          title={projectName}
          subtitle={project?.description ? undefined : "Review project details and manage delivery."}
          onBack={() => navigate(-1)}
        />

        <Card bodyClassName="project-detail-screen__tabs flex flex-wrap items-center gap-2">
          {(
            [
              { key: "overview", label: "Overview" },
              { key: "requirements", label: "Requirements" },
              { key: "specs", label: "Specs" },
              { key: "discovery", label: "Discovery" },
              { key: "preDiscovery", label: "Pre-discovery" },
              { key: "meetings", label: "Meetings" },
              { key: "workplan", label: "Workplan" },
              { key: "coverage", label: "Coverage" },
              { key: "brief", label: "Project Brief" },
              { key: "bench", label: "Bench" },
              ...(cockpitEnabled ? ([{ key: "cockpit", label: "Cockpit" }] as const) : []),
            ] satisfies { key: DetailTabKey; label: string }[]
          ).map((tab) => (
            <Button
              key={tab.key}
              type="button"
              className="project-detail-screen__tab-btn"
              variant={activeTab === tab.key ? "primary" : "secondary"}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </Button>
          ))}
        </Card>

        {activeTab === "overview"
          ? overviewContent
          : activeTab === "requirements"
            ? requirementsContent
            : activeTab === "specs"
              ? specsContent
              : activeTab === "discovery"
                ? discoveryContent
                : activeTab === "preDiscovery"
                  ? preDiscoveryContent
                  : activeTab === "meetings"
                    ? meetingsContent
                    : activeTab === "workplan"
                      ? workplanContent
                    : activeTab === "coverage"
                      ? coverageContent
                      : activeTab === "brief"
                        ? briefContent
                        : activeTab === "bench"
                          ? benchContent
                          : activeTab === "cockpit" && cockpitEnabled
                            ? projectId
                              ? (
                                  <div className="cockpit-fullwidth">
                                    <ProjectCockpitTab projectId={projectId} />
                                  </div>
                                )
                            : (
                                  <Card>
                                    <div className="p-4 text-sm text-slate-700">Project id is required to load cockpit.</div>
                                  </Card>
                                )
                            : briefContent}
      </div>
    </PageShell>
  );
}
